/**
 * FooterSection
 * 
 * Renders footer on all pages of the PDF.
 * Contains company contact information and pagination.
 */

import { jsPDF } from 'jspdf'
import { CONTROL_CARD_CONFIG, CONTROL_CARD_TEXT } from '../../config'
import { PDFRenderer } from '../../infrastructure'

/**
 * Footer section renderer
 */
export class FooterSection {
  private readonly pdfRenderer: PDFRenderer

  constructor(
    private readonly config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG
  ) {
    this.pdfRenderer = new PDFRenderer(config)
  }

  /**
   * Render footer on all pages
   */
  render(doc: jsPDF): void {
    const pageCount = this.getPageCount(doc)
    const pageDimensions = this.pdfRenderer.getPageDimensions()

    const bottomMargin = this.config.layout.margin.bottom
    const footerY = pageDimensions.height - bottomMargin
    const centerX = pageDimensions.width / 2
    
    // Pagination positioned above footer text
    const paginationY = footerY - 4 // 4mm above footer text

    // Render footer and pagination on each page
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      this.setPage(doc, pageNumber)

      // Render pagination only if more than one page
      if (pageCount > 1) {
        doc.setFont(
          this.config.typography.footer.font,
          this.config.typography.footer.weight
        )
        doc.setFontSize(this.config.typography.small.size) // Slightly larger than footer
        doc.setTextColor(...this.config.colors.text)
        
        const paginationText = `${pageNumber}/${pageCount}`
        doc.text(paginationText, centerX, paginationY, { align: 'center' })
      }

      // Render footer text
      doc.setFont(
        this.config.typography.footer.font,
        this.config.typography.footer.weight
      )
      doc.setFontSize(this.config.typography.footer.size)
      doc.setTextColor(...this.config.colors.black)

      doc.text(CONTROL_CARD_TEXT.footer, centerX, footerY, { align: 'center' })
    }
  }

  /**
   * Get number of pages in document
   */
  private getPageCount(doc: jsPDF): number {
    return typeof (doc as any).getNumberOfPages === 'function'
      ? (doc as any).getNumberOfPages()
      : 1
  }

  /**
   * Set current page
   */
  private setPage(doc: jsPDF, pageNumber: number): void {
    if (typeof (doc as any).setPage === 'function') {
      ;(doc as any).setPage(pageNumber)
    }
  }
}
